import { useMemo } from 'react'

import type { InventoryStockStatusType, StockMovementTypeType } from '@/api/admin-inventory-types'
import { useListInventoryLocations } from '@/api/react-query'
import { useAppForm } from '@/components/form/form'
import { AdvancedFilterDrawer } from '@/components/table/advanced-filter-drawer'
import { useI18n } from '@/i18n/i18n-react'

const STOCK_STATUSES = ['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK'] as const

export type InventoryListFilters = {
	inventoryLocationUuid?: string
	stockStatus?: InventoryStockStatusType
}

export function InventoryListFilterDrawer({
	open,
	onOpenChange,
	defaultValues,
	onApply
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	defaultValues: InventoryListFilters
	onApply: (filters: InventoryListFilters) => void
}) {
	const { L } = useI18n()
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })

	const inventoryLocationOptions = useMemo(
		() =>
			(inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
				value: location.uuid,
				label: location.name
			})),
		[inventoryLocations.data]
	)

	const stockStatusOptions = useMemo(
		() =>
			STOCK_STATUSES.map(status => ({
				value: status,
				label: L.Inventory.Status[status]()
			})),
		[L]
	)

	const form = useAppForm({
		defaultValues: {
			inventoryLocationUuid: defaultValues.inventoryLocationUuid ?? '',
			stockStatus: defaultValues.stockStatus ?? ''
		},
		onSubmit: async ({ value }) => {
			onApply({
				inventoryLocationUuid: value.inventoryLocationUuid || undefined,
				stockStatus: (value.stockStatus as InventoryStockStatusType) || undefined
			})
			onOpenChange(false)
		}
	})

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					onApply({})
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
				title={L.General.AdvancedFilters()}
			>
				<form.AppField name='inventoryLocationUuid'>
					{field => (
						<field.SelectField
							label={L.Inventory.Filter.InventoryLocation()}
							options={inventoryLocationOptions}
						/>
					)}
				</form.AppField>
				<form.AppField name='stockStatus'>
					{field => <field.SelectField label={L.Inventory.Filter.Status()} options={stockStatusOptions} />}
				</form.AppField>
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}

const MOVEMENT_TYPES = [
	'ADJUSTMENT',
	'RECEIPT',
	'TRANSFER_IN',
	'TRANSFER_OUT',
	'ORDER_DEDUCTION',
	'ORDER_RETURN'
] as const

export type MovementFilters = {
	inventoryLocationUuid?: string
	type?: StockMovementTypeType
	fromDate?: number
	toDate?: number
}

export function MovementFilterDrawer({
	open,
	onOpenChange,
	defaultValues,
	onApply
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	defaultValues: MovementFilters
	onApply: (filters: MovementFilters) => void
}) {
	const { L } = useI18n()
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })

	const inventoryLocationOptions = useMemo(
		() =>
			(inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
				value: location.uuid,
				label: location.name
			})),
		[inventoryLocations.data]
	)

	const movementTypeOptions = useMemo(
		() =>
			MOVEMENT_TYPES.map(type => ({
				value: type,
				label: L.Inventory.MovementType[type]()
			})),
		[L]
	)

	const form = useAppForm({
		defaultValues: {
			inventoryLocationUuid: defaultValues.inventoryLocationUuid ?? '',
			type: defaultValues.type ?? '',
			fromDate: defaultValues.fromDate,
			toDate: defaultValues.toDate
		},
		onSubmit: async ({ value }) => {
			onApply({
				inventoryLocationUuid: value.inventoryLocationUuid || undefined,
				type: (value.type as StockMovementTypeType) || undefined,
				fromDate: value.fromDate,
				toDate: value.toDate
			})
			onOpenChange(false)
		}
	})

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					onApply({})
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
				title={L.General.AdvancedFilters()}
			>
				<form.AppField name='inventoryLocationUuid'>
					{field => (
						<field.SelectField
							label={L.Inventory.Filter.InventoryLocation()}
							options={inventoryLocationOptions}
						/>
					)}
				</form.AppField>
				<form.AppField name='type'>
					{field => <field.SelectField label={L.Inventory.Filter.Type()} options={movementTypeOptions} />}
				</form.AppField>
				<form.AppField name='fromDate'>
					{field => <field.DateField label={L.Inventory.Column.From()} optional />}
				</form.AppField>
				<form.AppField name='toDate'>
					{field => <field.DateField label={L.Inventory.Column.To()} optional />}
				</form.AppField>
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}
